<script setup>
import { computed } from 'vue'
import { Head, usePage, Link } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import PartnersLayout from '@/Layouts/PartnersLayout.vue'
import Icon from '@/Components/UI/Icon.vue'

defineOptions({ layout: PartnersLayout })

const { t } = useI18n()
const page = usePage()
const base = computed(() => page.props.base || '')

defineProps({
  types:         { type: Array, default: () => [] },
  portal_logins: { type: Array, default: () => [] }
})

const steps = ['register', 'approve', 'manage']
const benefits = [
  { key: 'reach',    glyph: 'users' },
  { key: 'prices',   glyph: 'price' },
  { key: 'funnel',   glyph: 'chat' },
  { key: 'verified', glyph: 'verified' }
]
const trust = [
  { key: 'reach',    glyph: 'pin' },
  { key: 'verified', glyph: 'verified' },
  { key: 'fast',     glyph: 'bolt' },
  { key: 'control',  glyph: 'settings' }
]
const typeGlyph  = { supplier: 'rental', vendor: 'compass', host: 'home' }
const loginGlyph = { supplier: 'rental', vendor: 'compass', host: 'home', driver: 'truck' }

function scrollTo(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
}

// Inline sun watermark, matching the public home's hero motif.
const SUN_PATHS = [
  'M60 6v24', 'M60 90v24', 'M6 60h24', 'M90 60h24',
  'M22 22l16 16', 'M82 82l16 16', 'M98 22l-16 16', 'M38 82l-16 16'
]
</script>

<template>
  <Head><title>{{ t('partners.index.head_title') }}</title></Head>

  <!-- HERO -->
  <section class="hero">
    <svg
      class="hero__sun"
      viewBox="0 0 120 120"
      aria-hidden="true"
    >
      <g
        stroke="currentColor"
        stroke-width="6"
        stroke-linecap="round"
      >
        <path
          v-for="(d, i) in SUN_PATHS"
          :key="i"
          :d="d"
        />
      </g>
      <circle
        cx="60"
        cy="60"
        r="22"
        fill="currentColor"
      />
    </svg>
    <div class="hero__inner">
      <p class="hero__eyebrow">{{ t('partners.index.hero_eyebrow') }}</p>
      <h1 class="hero__title">{{ t('partners.index.title') }}</h1>
      <p class="hero__lede">{{ t('partners.index.hero_subtitle') }}</p>
      <div class="hero__actions">
        <button
          type="button"
          class="btn btn--light"
          @click="scrollTo('partner-types')"
        >{{ t('partners.index.hero_cta') }} →</button>
        <button
          type="button"
          class="btn btn--ghost"
          @click="scrollTo('partner-logins')"
        >{{ t('partners.index.hero_cta_secondary') }} ↓</button>
      </div>
    </div>
  </section>

  <!-- WHY PARTNER (trust-strip styled) -->
  <section class="band band--surface">
    <div class="band__inner">
      <p class="kicker">{{ t('partners.index.why_kicker') }}</p>
      <h2 class="band__title">{{ t('partners.index.why_title') }}</h2>
      <div class="strip">
        <div
          v-for="b in benefits"
          :key="b.key"
          class="strip-item"
        >
          <span class="strip-item__icon"><Icon :name="b.glyph" :size="24" /></span>
          <div class="strip-item__text">
            <h3 class="strip-item__title">{{ t(`partners.index.benefits.${b.key}.title`) }}</h3>
            <p class="strip-item__sub">{{ t(`partners.index.benefits.${b.key}.body`) }}</p>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- PARTNER TYPES — bold gradient promo cards (centerpiece) -->
  <section
    id="partner-types"
    class="band"
  >
    <div class="band__inner">
      <p class="kicker">{{ t('partners.index.types_kicker') }}</p>
      <h2 class="band__title">{{ t('partners.index.types_title') }}</h2>
      <p class="band__lede">{{ t('partners.index.lede') }}</p>
      <div class="promos">
        <Link
          v-for="ty in types"
          :key="ty"
          :href="`${base}/register?type=${ty}`"
          :class="['promo', `promo--${ty}`]"
        >
          <Icon
            :name="typeGlyph[ty]"
            :size="190"
            :stroke="1.4"
            class="promo__glyph"
          />
          <div class="promo__content">
            <p class="promo__eyebrow">{{ t(`partners.types.${ty}.name`) }}</p>
            <h3 class="promo__head">{{ t(`partners.types.${ty}.tagline`) }}</h3>
            <p class="promo__sub">{{ t(`partners.types.${ty}.value`) }}</p>
            <span class="promo__cta">{{ t('partners.index.type_cta', { type: t(`partners.types.${ty}.name`) }) }} →</span>
          </div>
        </Link>
      </div>
    </div>
  </section>

  <!-- HOW IT WORKS -->
  <section class="band band--surface">
    <div class="band__inner">
      <p class="kicker">{{ t('partners.index.how_kicker') }}</p>
      <h2 class="band__title">{{ t('partners.index.how_title') }}</h2>
      <ol class="steps">
        <li
          v-for="(s, i) in steps"
          :key="s"
          class="step"
        >
          <span class="step__n">{{ i + 1 }}</span>
          <h3 class="step__t">{{ t(`partners.index.steps.${s}.title`) }}</h3>
          <p class="step__b">{{ t(`partners.index.steps.${s}.body`) }}</p>
        </li>
      </ol>
    </div>
  </section>

  <!-- WHY GET PHUKET (trust-strip styled) -->
  <section class="band">
    <div class="band__inner">
      <p class="kicker">{{ t('partners.index.whyus_kicker') }}</p>
      <h2 class="band__title">{{ t('partners.index.whyus_title') }}</h2>
      <div class="strip">
        <div
          v-for="x in trust"
          :key="x.key"
          class="strip-item"
        >
          <span class="strip-item__icon strip-item__icon--second"><Icon :name="x.glyph" :size="24" /></span>
          <div class="strip-item__text">
            <h3 class="strip-item__title">{{ t(`partners.index.trust.${x.key}.title`) }}</h3>
            <p class="strip-item__sub">{{ t(`partners.index.trust.${x.key}.body`) }}</p>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ALREADY A PARTNER -->
  <section
    id="partner-logins"
    class="band band--surface"
  >
    <div class="band__inner">
      <p class="kicker">{{ t('partners.index.logins_kicker') }}</p>
      <h2 class="band__title">{{ t('partners.index.login_title') }}</h2>
      <div class="logins">
        <a
          v-for="login in portal_logins"
          :key="login.key"
          :href="login.url"
          class="login"
        >
          <span class="login__ic"><Icon :name="loginGlyph[login.key]" :size="26" /></span>
          <span class="login__label">{{ t(`partners.logins.${login.key}`) }}</span>
        </a>
      </div>
      <p class="login__note">{{ t('partners.index.login_note') }}</p>
    </div>
  </section>
</template>

<style scoped>
/* Shared wide inner column — matches getphuket.com home (96rem). */
.hero, .band { width: 100%; }
.hero__inner, .band__inner { max-width: 96rem; margin: 0 auto; padding-inline: clamp(1.25rem, 4vw, 3rem); }
.band { padding-block: clamp(2.75rem, 6vw, 4.75rem); }
.band--surface { background: var(--surface); }

.kicker { font-size: 0.8125rem; letter-spacing: 0.2em; text-transform: uppercase; font-weight: 700; color: var(--accent); text-align: center; margin: 0 0 0.6rem; }
.band__title { font-family: var(--display); font-weight: 800; font-size: clamp(1.75rem, 3.4vw, 2.5rem); letter-spacing: -0.02em; color: var(--ink); text-align: center; margin: 0 0 0.5rem; }
.band__lede { text-align: center; color: var(--inkSoft); font-size: clamp(1rem, 1.5vw, 1.15rem); line-height: 1.5; max-width: 40rem; margin: 0 auto 2.25rem; }

/* HERO */
.hero { position: relative; overflow: hidden; color: var(--accentInk); background: radial-gradient(120% 130% at 82% 12%, color-mix(in oklch, var(--accent) 76%, white) 0%, var(--accent) 44%, color-mix(in oklch, var(--accent) 70%, var(--second)) 100%); }
.hero__sun { position: absolute; top: -8rem; right: -4rem; width: 34rem; height: 34rem; color: var(--accentInk); opacity: 0.14; pointer-events: none; }
.hero__inner { position: relative; padding-top: clamp(3.5rem, 10vw, 8rem); padding-bottom: clamp(3rem, 8vw, 6.5rem); text-align: center; }
.hero__eyebrow { font-size: 0.8125rem; letter-spacing: 0.22em; text-transform: uppercase; font-weight: 600; margin: 0 0 1.1rem; opacity: 0.92; }
.hero__title { font-family: var(--display); font-weight: 800; font-size: clamp(2.5rem, 6vw, 4.5rem); line-height: 1.03; letter-spacing: -0.03em; margin: 0 auto; max-width: 18ch; }
.hero__lede { font-size: clamp(1.0625rem, 1.8vw, 1.375rem); line-height: 1.5; margin: 1.4rem auto 0; max-width: 44ch; opacity: 0.95; }
.hero__actions { display: flex; flex-wrap: wrap; gap: 0.875rem; justify-content: center; margin-top: 2.25rem; }
.btn { display: inline-flex; align-items: center; gap: 0.4rem; padding: 0.95rem 1.7rem; border-radius: 999px; border: 0; cursor: pointer; font-family: var(--display); font-weight: 700; font-size: 1.0625rem; transition: transform 150ms ease, background 150ms ease; }
.btn--light { background: var(--surface); color: var(--accent); }
.btn--light:hover { transform: translateY(-2px); }
.btn--ghost { background: color-mix(in oklch, var(--accentInk) 14%, transparent); color: var(--accentInk); border: 1px solid color-mix(in oklch, var(--accentInk) 45%, transparent); }
.btn--ghost:hover { background: color-mix(in oklch, var(--accentInk) 22%, transparent); }

/* TRUST-STRIP rows (benefits + why-us) */
.strip { display: grid; grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr)); gap: 1.5rem 2rem; margin-top: 0.5rem; }
.strip-item { display: flex; align-items: flex-start; gap: 0.9rem; }
.strip-item__icon { width: 2.85rem; height: 2.85rem; border-radius: 0.85rem; flex: none; display: grid; place-items: center; background: color-mix(in oklch, var(--accent) 13%, var(--surface)); color: var(--accent); }
.strip-item__icon--second { background: color-mix(in oklch, var(--second) 14%, var(--surface)); color: var(--second); }
/* Center the icon badge on the title's first line — the 2.85rem icon is taller
   than the title line, so without this nudge the icon hangs ~10px low. */
.strip-item__text { padding-top: 0.6rem; }
.strip-item__title { font-family: var(--display); font-weight: 700; font-size: 1.1rem; margin: 0; color: var(--ink); letter-spacing: -0.01em; }
.strip-item__sub { margin: 0.25rem 0 0; font-size: 0.95rem; line-height: 1.45; color: var(--inkSoft); }

/* PROMO CARDS — partner types (centerpiece) */
.promos { display: grid; grid-template-columns: repeat(auto-fit, minmax(18rem, 1fr)); gap: 1.5rem; }
.promo { position: relative; overflow: hidden; isolation: isolate; min-height: 21rem; border-radius: 1.75rem; display: flex; align-items: flex-end; padding: 2rem; text-decoration: none; color: var(--accentInk); transition: transform 180ms ease, box-shadow 180ms ease; }
.promo:hover { transform: translateY(-5px); box-shadow: 0 26px 50px -24px color-mix(in oklch, var(--ink) 55%, transparent); }
.promo__glyph { position: absolute; top: -2.5rem; right: -2.5rem; color: var(--accentInk); opacity: 0.16; pointer-events: none; z-index: -1; }
.promo__content { color: inherit; }
.promo__eyebrow { font-size: 0.8rem; letter-spacing: 0.18em; text-transform: uppercase; font-weight: 700; margin: 0 0 0.5rem; opacity: 0.9; }
.promo__head { font-family: var(--display); font-weight: 800; font-size: clamp(1.55rem, 2.4vw, 2rem); line-height: 1.08; letter-spacing: -0.02em; margin: 0; }
.promo__sub { margin: 0.65rem 0 1.3rem; font-size: 1.0625rem; line-height: 1.45; opacity: 0.94; max-width: 28ch; }
.promo__cta { display: inline-flex; align-items: center; gap: 0.4rem; padding: 0.7rem 1.3rem; border-radius: 999px; font-family: var(--display); font-weight: 700; font-size: 1rem; background: var(--ink); color: var(--accentInk); }
.promo--supplier { background: radial-gradient(120% 130% at 82% 12%, color-mix(in oklch, var(--accent) 80%, white) 0%, var(--accent) 48%, color-mix(in oklch, var(--accent) 75%, black) 100%); }
.promo--vendor { background: radial-gradient(120% 130% at 82% 12%, color-mix(in oklch, var(--second) 78%, white) 0%, var(--second) 48%, color-mix(in oklch, var(--second) 72%, black) 100%); }
.promo--host { color: var(--bg); background: linear-gradient(155deg, color-mix(in oklch, var(--ink) 90%, var(--second)) 0%, var(--ink) 70%); }
.promo--host .promo__glyph { color: var(--bg); }
.promo--host .promo__cta { background: var(--accent); }

/* STEPS */
.steps { list-style: none; margin: 0.5rem 0 0; padding: 0; display: grid; grid-template-columns: 1fr; gap: 1.75rem; }
@media (min-width: 48rem) { .steps { grid-template-columns: repeat(3, 1fr); } }
.step__n { display: inline-flex; align-items: center; justify-content: center; width: 2.4rem; height: 2.4rem; border-radius: 50%; background: var(--accent); color: var(--accentInk); font-family: var(--display); font-weight: 800; font-size: 1.125rem; margin-bottom: 0.8rem; }
.step__t { font-family: var(--display); font-weight: 700; color: var(--ink); font-size: 1.125rem; margin: 0 0 0.35rem; }
.step__b { font-size: 0.975rem; color: var(--inkSoft); line-height: 1.5; margin: 0; }

/* LOGINS */
.logins { display: grid; grid-template-columns: repeat(auto-fit, minmax(13rem, 1fr)); gap: 0.875rem; max-width: 60rem; margin: 0.5rem auto 0; }
.login { display: flex; flex-direction: column; align-items: center; gap: 0.6rem; padding: 1.5rem 1rem; border: 1px solid var(--line); border-radius: 1rem; background: var(--bg); color: var(--ink); text-decoration: none; font-weight: 600; font-size: 0.95rem; text-align: center; transition: border-color 150ms ease, transform 150ms ease, box-shadow 150ms ease; }
.login:hover { border-color: color-mix(in oklch, var(--accent) 55%, var(--line)); transform: translateY(-2px); box-shadow: 0 16px 30px -22px color-mix(in oklch, var(--ink) 55%, transparent); }
.login__ic { display: inline-flex; width: 3rem; height: 3rem; border-radius: 0.85rem; align-items: center; justify-content: center; background: color-mix(in oklch, var(--accent) 12%, var(--surface)); color: var(--accent); }
.login__label { line-height: 1.3; }
.login__note { font-size: 0.95rem; color: var(--muted); text-align: center; margin: 1.5rem auto 0; max-width: 38rem; }
</style>
